import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import AICallAnalytics from '../../components/AICallAnalytics';
import {
  Phone,
  Plus,
  Play,
  Pause,
  Settings,
  Trash2,
  Edit,
  Loader2,
  Users,
  Calendar,
  Clock,
  PhoneCall,
  PhoneOff,
  Voicemail,
  Mail,
  BarChart3,
  Volume2,
  Bot,
  Sparkles,
  Target,
  TrendingUp,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

axios.defaults.withCredentials = true;

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
};

const outcomeIcons = {
  meeting_booked: <CheckCircle2 className="h-4 w-4 text-green-500" />,
  demo_scheduled: <Calendar className="h-4 w-4 text-blue-500" />,
  voicemail_dropped: <Voicemail className="h-4 w-4 text-orange-500" />,
  no_answer: <PhoneOff className="h-4 w-4 text-gray-500" />,
  not_interested: <XCircle className="h-4 w-4 text-red-500" />,
  callback_requested: <Clock className="h-4 w-4 text-purple-500" />,
};

export default function AICallCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Main page tab state
  const [mainTab, setMainTab] = useState('campaigns');
  
  // Voice selection
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [selectedGender, setSelectedGender] = useState('');
  
  // Voicemail templates
  const [voicemailTemplates, setVoicemailTemplates] = useState([]);
  
  // Email templates  
  const [emailTemplates, setEmailTemplates] = useState([]);
  
  // SMS templates
  const [smsTemplates, setSmsTemplates] = useState([]);
  
  // Test call state
  const [testPhoneNumber, setTestPhoneNumber] = useState('7328001313');
  const [testCallLoading, setTestCallLoading] = useState(false);
  
  // Voice preview state
  const [previewingVoiceId, setPreviewingVoiceId] = useState(null);
  const [previewAudioRef] = useState(new Audio());
  
  // Greeting audio state
  const [greetingAudioUrl, setGreetingAudioUrl] = useState('');
  const [generatingGreeting, setGeneratingGreeting] = useState(false);
  const [playingGreeting, setPlayingGreeting] = useState(false);
  const [greetingAudioRef] = useState(new Audio());
  
  // Leads state for campaign
  const [allLeads, setAllLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    voice_id: '',
    voice_name: '',
    system_prompt: getDefaultSystemPrompt(),
    opening_message: '',
    greeting_audio_url: '',
    greeting_audio_filename: '',
    greeting_audio_name: '',
    voicemail_template_id: '',
    follow_up_email_template_id: '',
    send_email_on_voicemail: true,
    send_email_on_no_answer: true,
    send_email_on_interested: true,
    send_email_on_callback: true,
    // Outcome-specific email templates
    email_template_voicemail: '',
    email_template_no_answer: '',
    email_template_interested: '',
    email_template_callback: '',
    email_template_meeting_booked: '',
    // SMS follow-up
    send_sms_on_voicemail: false,
    send_sms_on_no_answer: false,
    sms_template_voicemail: '',
    sms_template_no_answer: '',
    // Scheduling
    call_window_start: '09:00',
    call_window_end: '17:00',
    call_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    max_attempts: 3,
    retry_interval_hours: 24,
    lead_ids: [],
    // Predictive Dialer
    dialer_mode: 'preview',
    max_concurrent_calls: 4,
    pacing_ratio_target: 2.5,
    abandonment_cap_pct: 3.0,
    pitch_grace_seconds: 8,
    voicemail_media_id: '',
    voicemail_telnyx_media_name: '',
    pitch_media_id: '',
    pitch_telnyx_media_name: '',
  });
  const [audioLibrary, setAudioLibrary] = useState({ voicemail: [], pitch: [] });
  const [uploadingAudio, setUploadingAudio] = useState({ voicemail: false, pitch: false });
  const navigate = (path) => { window.location.href = path; };

  useEffect(() => {
    fetchCampaigns();
    // Auto-load silently — if ElevenLabs key is missing the user gets a toast
    // only when they actually click the Voice tab / Generate buttons.
    fetchVoices('', false);
    fetchTemplates();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/api/ai-calls/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchVoices = async (gender = '', showToastOnError = true) => {
    setLoadingVoices(true);
    try {
      const url = gender ? `${API}/api/elevenlabs/voices?gender=${gender}` : `${API}/api/elevenlabs/voices`;
      const response = await axios.get(url);
      setVoices(response.data.voices || []);
    } catch (error) {
      console.error('Failed to load voices:', error?.response?.data || error);
      if (showToastOnError) {
        toast.error(error.response?.data?.detail || 'Failed to load ElevenLabs voices. Please check API configuration.');
      }
    } finally {
      setLoadingVoices(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const [vmRes, emailRes, smsRes] = await Promise.all([
        axios.get(`${API}/api/templates?type=voicemail_drop`),
        axios.get(`${API}/api/templates?type=email`),
        axios.get(`${API}/api/templates?type=sms`)
      ]);
      setVoicemailTemplates(vmRes.data || []);
      setEmailTemplates(emailRes.data || []);
      setSmsTemplates(smsRes.data || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const fetchLeads = async () => {
    setLoadingLeads(true);
    try {
      // Fetch all leads with a high limit
      const response = await axios.get(`${API}/api/leads?limit=500`);
      // Handle both array and paginated response formats
      const leadsData = response.data?.items || response.data || [];
      setAllLeads(leadsData);
    } catch (error) {
      console.error('Failed to load leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
    fetchVoices(gender);
  };

  const fetchAudioLibrary = async () => {
    try {
      const [vm, pt] = await Promise.all([
        axios.get(`${API}/api/ai-calls/audio-media?kind=voicemail`),
        axios.get(`${API}/api/ai-calls/audio-media?kind=pitch`),
      ]);
      setAudioLibrary({ voicemail: vm.data || [], pitch: pt.data || [] });
    } catch (e) {
      console.error('fetchAudioLibrary', e);
    }
  };

  const uploadAudioClip = async (kind, file, name) => {
    if (!file) return;
    setUploadingAudio((prev) => ({ ...prev, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append('audio', file);
      fd.append('kind', kind);
      fd.append('name', name || file.name);
      const resp = await axios.post(`${API}/api/ai-calls/audio-media`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const doc = resp.data;
      if (!doc.telnyx_upload_ok) {
        toast.warning('Uploaded locally but Telnyx Media Storage upload failed — check TELNYX_API_KEY');
      } else {
        toast.success(`${kind === 'voicemail' ? 'Voicemail' : 'Pitch'} clip uploaded`);
      }
      setFormData((prev) => ({
        ...prev,
        [`${kind}_media_id`]: doc.id,
        [`${kind}_telnyx_media_name`]: doc.telnyx_media_name || '',
      }));
      await fetchAudioLibrary();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to upload ${kind}`);
    } finally {
      setUploadingAudio((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const selectAudioClip = (kind, clipId) => {
    const clip = (audioLibrary[kind] || []).find((c) => c.id === clipId);
    if (!clip) return;
    setFormData((prev) => ({
      ...prev,
      [`${kind}_media_id`]: clip.id,
      [`${kind}_telnyx_media_name`]: clip.telnyx_media_name || '',
    }));
  };

  const renameAudioClip = async (kind, clipId, currentName) => {
    const next = window.prompt('Rename clip:', currentName || '');
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed) return;
    if (trimmed === currentName) return;
    try {
      await axios.patch(`${API}/api/ai-calls/audio-media/${clipId}`, { name: trimmed });
      toast.success('Renamed');
      await fetchAudioLibrary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rename failed');
    }
  };

  const deleteAudioClip = async (kind, clipId) => {
    if (!window.confirm('Delete this clip? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/ai-calls/audio-media/${clipId}`);
      toast.success('Clip deleted');
      // If the deleted clip was selected on this campaign, clear it
      if (formData[`${kind}_media_id`] === clipId) {
        setFormData((prev) => ({
          ...prev,
          [`${kind}_media_id`]: '',
          [`${kind}_telnyx_media_name`]: '',
        }));
      }
      await fetchAudioLibrary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const launchLiveSession = async (campaignId) => {
    try {
      const resp = await axios.post(`${API}/api/ai-calls/campaigns/${campaignId}/live-session/start`, {});
      toast.success(`Dialing ${resp.data.enqueued} leads at ${resp.data.max_concurrent_calls} concurrent`);
      navigate(`/admin/ai-calls/live/${campaignId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to launch session');
    }
  };

  const toggleLeadSelection = (leadId) => {
    setFormData(prev => {
      const currentIds = prev.lead_ids || [];
      if (currentIds.includes(leadId)) {
        return { ...prev, lead_ids: currentIds.filter(id => id !== leadId) };
      } else {
        return { ...prev, lead_ids: [...currentIds, leadId] };
      }
    });
  };

  const selectAllFilteredLeads = () => {
    const filteredIds = filteredLeads.map(l => l.id);
    setFormData(prev => ({
      ...prev,
      lead_ids: [...new Set([...(prev.lead_ids || []), ...filteredIds])]
    }));
  };

  const clearAllLeads = () => {
    setFormData(prev => ({ ...prev, lead_ids: [] }));
  };

  // Filter leads based on search query
  const filteredLeads = useMemo(() => {
    if (!leadSearchQuery.trim()) return allLeads;
    const query = leadSearchQuery.toLowerCase();
    return allLeads.filter(lead => 
      (lead.first_name || '').toLowerCase().includes(query) ||
      (lead.last_name || '').toLowerCase().includes(query) ||
      (lead.company_name || '').toLowerCase().includes(query) ||
      (lead.work_email || '').toLowerCase().includes(query) ||
      (lead.work_direct_phone || '').includes(query) ||
      (lead.mobile_phone || '').includes(query)
    );
  }, [allLeads, leadSearchQuery]);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    // AI voice/prompt/opening are only required in preview (AI conversation) mode.
    // Predictive mode plays pre-recorded voicemail + pitch clips; no AI voice needed.
    if (formData.dialer_mode !== 'predictive') {
      if (!formData.voice_id) {
        toast.error('Please select a voice');
        return;
      }
      if (!formData.system_prompt.trim()) {
        toast.error('System prompt is required');
        return;
      }
      if (!formData.opening_message.trim()) {
        toast.error('Opening message is required');
        return;
      }
    }

    setSaving(true);
    try {
      if (editingCampaign) {
        await axios.put(`${API}/api/ai-calls/campaigns/${editingCampaign.id}`, formData);
        toast.success('Campaign updated');
      } else {
        await axios.post(`${API}/api/ai-calls/campaigns`, formData);
        toast.success('Campaign created');
      }
      setCreateDialogOpen(false);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      await axios.post(`${API}/api/ai-calls/campaigns/${campaignId}/status?status=${newStatus}`);
      toast.success(`Campaign ${newStatus}`);
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await axios.delete(`${API}/api/ai-calls/campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      voice_id: '',
      voice_name: '',
      system_prompt: getDefaultSystemPrompt(),
      opening_message: '',
      voicemail_template_id: '',
      follow_up_email_template_id: '',
      send_email_on_voicemail: true,
      send_email_on_no_answer: true,
      send_email_on_interested: true,
      send_email_on_callback: true,
      email_template_voicemail: '',
      email_template_no_answer: '',
      email_template_interested: '',
      email_template_callback: '',
      email_template_meeting_booked: '',
      send_sms_on_voicemail: false,
      send_sms_on_no_answer: false,
      sms_template_voicemail: '',
      sms_template_no_answer: '',
      call_window_start: '09:00',
      call_window_end: '17:00',
      call_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      max_attempts: 3,
      retry_interval_hours: 24,
      lead_ids: [],
      greeting_audio_url: '',
      greeting_audio_filename: '',
      greeting_audio_name: '',
    });
    setEditingCampaign(null);
    setSelectedGender('');
    setLeadSearchQuery('');
    setGreetingAudioUrl('');
    setPreviewingVoiceId(null);
    setPlayingGreeting(false);
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    
    // Reconstruct audio URL from filename if we have a filename
    let audioUrl = campaign.greeting_audio_url || '';
    if (!audioUrl && campaign.greeting_audio_filename) {
      audioUrl = `${API}/api/ai-calls/audio/${campaign.greeting_audio_filename}`;
    }
    
    setFormData({
      name: campaign.name,
      description: campaign.description || '',
      voice_id: campaign.voice_id,
      voice_name: campaign.voice_name || '',
      system_prompt: campaign.system_prompt,
      opening_message: campaign.opening_message,
      greeting_audio_url: audioUrl,
      greeting_audio_filename: campaign.greeting_audio_filename || '',
      greeting_audio_name: campaign.greeting_audio_name || '',
      voicemail_template_id: campaign.voicemail_template_id || '',
      follow_up_email_template_id: campaign.follow_up_email_template_id || '',
      send_email_on_voicemail: campaign.send_email_on_voicemail ?? true,
      send_email_on_no_answer: campaign.send_email_on_no_answer ?? true,
      send_email_on_interested: campaign.send_email_on_interested ?? true,
      send_email_on_callback: campaign.send_email_on_callback ?? true,
      email_template_voicemail: campaign.email_template_voicemail || '',
      email_template_no_answer: campaign.email_template_no_answer || '',
      email_template_interested: campaign.email_template_interested || '',
      email_template_callback: campaign.email_template_callback || '',
      email_template_meeting_booked: campaign.email_template_meeting_booked || '',
      send_sms_on_voicemail: campaign.send_sms_on_voicemail ?? false,
      send_sms_on_no_answer: campaign.send_sms_on_no_answer ?? false,
      sms_template_voicemail: campaign.sms_template_voicemail || '',
      sms_template_no_answer: campaign.sms_template_no_answer || '',
      call_window_start: campaign.call_window_start,
      call_window_end: campaign.call_window_end,
      call_days: campaign.call_days,
      max_attempts: campaign.max_attempts,
      retry_interval_hours: campaign.retry_interval_hours,
      lead_ids: campaign.lead_ids || [],
      dialer_mode: campaign.dialer_mode || 'preview',
      max_concurrent_calls: campaign.max_concurrent_calls ?? 4,
      pacing_ratio_target: campaign.pacing_ratio_target ?? 2.5,
      abandonment_cap_pct: campaign.abandonment_cap_pct ?? 3.0,
      pitch_grace_seconds: campaign.pitch_grace_seconds ?? 8,
      voicemail_media_id: campaign.voicemail_media_id || '',
      voicemail_telnyx_media_name: campaign.voicemail_telnyx_media_name || '',
      pitch_media_id: campaign.pitch_media_id || '',
      pitch_telnyx_media_name: campaign.pitch_telnyx_media_name || '',
    });
    setGreetingAudioUrl(audioUrl);
    fetchLeads(); // Load leads when opening dialog
    fetchAudioLibrary();
    setCreateDialogOpen(true);
  };

  const generateAIOpeningMessage = async () => {
    try {
      const response = await axios.post(`${API}/api/elevenlabs/generate-script`, {
        user_name: 'JobTalk AI Assistant',
        user_phone: '(732) 394-1313',
        topic: formData.description || 'AI sales call for staffing companies'
      });
      setFormData(prev => ({ ...prev, opening_message: response.data.script }));
      toast.success('Opening message generated!');
    } catch (error) {
      toast.error('Failed to generate message');
    }
  };

  // Voice preview function
  const handleVoicePreview = async (voiceId, voiceName) => {
    if (previewingVoiceId === voiceId) {
      // Stop preview
      previewAudioRef.pause();
      previewAudioRef.currentTime = 0;
      setPreviewingVoiceId(null);
      return;
    }
    
    setPreviewingVoiceId(voiceId);
    try {
      const response = await axios.post(`${API}/api/ai-calls/voice-preview`, {
        voice_id: voiceId,
        text: `Hello, my name is ${voiceName}. I'm an AI sales assistant ready to help grow your business.`
      });
      
      previewAudioRef.src = response.data.audio_url;
      previewAudioRef.onended = () => setPreviewingVoiceId(null);
      previewAudioRef.onerror = () => {
        toast.error('Failed to play voice preview');
        setPreviewingVoiceId(null);
      };
      await previewAudioRef.play();
    } catch (error) {
      toast.error('Failed to generate voice preview');
      setPreviewingVoiceId(null);
    }
  };

  // Generate greeting audio
  const handleGenerateGreeting = async () => {
    if (!formData.voice_id) {
      toast.error('Please select a voice first');
      return;
    }
    if (!formData.opening_message.trim()) {
      toast.error('Please enter an opening message first');
      return;
    }
    
    setGeneratingGreeting(true);
    try {
      const labelToSend = (formData.greeting_audio_name || '').trim()
        || (formData.name ? `${formData.name} — greeting` : '');
      const response = await axios.post(`${API}/api/ai-calls/generate-greeting`, {
        voice_id: formData.voice_id,
        text: formData.opening_message,
        campaign_id: editingCampaign?.id,
        name: labelToSend || undefined,
      });

      // Construct URL using the API base
      const audioUrl = `${API}/api/ai-calls/audio/${response.data.filename}`;
      setGreetingAudioUrl(audioUrl);
      setFormData(prev => ({
        ...prev,
        greeting_audio_url: audioUrl,
        greeting_audio_filename: response.data.filename,
        greeting_audio_name: response.data.name || labelToSend || prev.greeting_audio_name,
      }));
      toast.success(`Greeting generated! Duration: ${response.data.duration_seconds}s, Cost: $${response.data.estimated_cost_usd}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate greeting audio');
    } finally {
      setGeneratingGreeting(false);
    }
  };

  // Delete greeting audio
  const handleDeleteGreeting = async () => {
    const filename = formData.greeting_audio_filename;
    if (!filename) {
      // Just clear the local state
      setGreetingAudioUrl('');
      setFormData(prev => ({ ...prev, greeting_audio_url: '', greeting_audio_filename: '', greeting_audio_name: '' }));
      return;
    }
    
    try {
      await axios.delete(`${API}/api/ai-calls/audio/${filename}`);
      setGreetingAudioUrl('');
      setFormData(prev => ({ ...prev, greeting_audio_url: '', greeting_audio_filename: '', greeting_audio_name: '' }));
      toast.success('Greeting audio deleted');
    } catch (error) {
      // Even if delete fails on server, clear local state
      setGreetingAudioUrl('');
      setFormData(prev => ({ ...prev, greeting_audio_url: '', greeting_audio_filename: '', greeting_audio_name: '' }));
    }
  };

  // Play/pause greeting audio
  const handlePlayGreeting = () => {
    const audioUrl = greetingAudioUrl || formData.greeting_audio_url;
    if (!audioUrl) {
      toast.error('Generate greeting audio first');
      return;
    }
    
    if (playingGreeting) {
      greetingAudioRef.pause();
      greetingAudioRef.currentTime = 0;
      setPlayingGreeting(false);
    } else {
      greetingAudioRef.src = audioUrl;
      greetingAudioRef.onended = () => setPlayingGreeting(false);
      greetingAudioRef.onerror = (e) => {
        console.error('Audio error:', e);
        toast.error('Failed to play greeting audio');
        setPlayingGreeting(false);
      };
      greetingAudioRef.play().catch(err => {
        console.error('Play error:', err);
        toast.error('Failed to play audio');
        setPlayingGreeting(false);
      });
      setPlayingGreeting(true);
    }
  };

  const handleTestCall = async () => {
    if (!formData.voice_id) {
      toast.error('Please select a voice first');
      return;
    }
    if (!formData.opening_message.trim()) {
      toast.error('Please enter an opening message');
      return;
    }
    if (!testPhoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }
    
    // Check if greeting audio is generated
    const greetingUrl = greetingAudioUrl || formData.greeting_audio_url;
    if (!greetingUrl) {
      toast.error('Please generate greeting audio first before testing');
      return;
    }
    
    // Format phone number
    const cleanPhone = testPhoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setTestCallLoading(true);
    try {
      const response = await axios.post(`${API}/api/ai-calls/test-call`, {
        phone_number: cleanPhone.length === 10 ? `+1${cleanPhone}` : `+${cleanPhone}`,
        voice_id: formData.voice_id,
        system_prompt: formData.system_prompt,
        opening_message: formData.opening_message,
        greeting_audio_url: greetingUrl
      });
      
      toast.success('Test call initiated! You should receive a call shortly.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate test call');
    } finally {
      setTestCallLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <Bot className="h-8 w-8 text-primary" />
            AI Voice Campaigns
          </h1>
          <p className="text-slate-600 mt-1">Create and manage AI-powered outbound sales calls</p>
        </div>
        {mainTab === 'campaigns' && (
          <Button onClick={() => { resetForm(); fetchLeads(); setCreateDialogOpen(true); }} data-testid="create-campaign-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Campaign
          </Button>
        )}
      </div>

      {/* Main Tabs: Campaigns / Analytics */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList>
          <TabsTrigger value="campaigns" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="mt-4">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-indigo-100">
                    <Users className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Leads</p>
                    <p className="text-2xl font-bold">{campaigns.reduce((sum, c) => sum + (c.total_leads || c.lead_ids?.length || 0), 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-blue-100">
                    <Phone className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Calls</p>
                    <p className="text-2xl font-bold">{campaigns.reduce((sum, c) => sum + (c.calls_made || 0), 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-green-100">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Meetings Booked</p>
                    <p className="text-2xl font-bold">{campaigns.reduce((sum, c) => sum + (c.meetings_booked || 0), 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-100">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Demos Scheduled</p>
                <p className="text-2xl font-bold">{campaigns.reduce((sum, c) => sum + (c.demos_scheduled || 0), 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-orange-100">
                <Voicemail className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Voicemails Dropped</p>
                <p className="text-2xl font-bold">{campaigns.reduce((sum, c) => sum + (c.voicemails_dropped || 0), 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns List */}
      <Card>
        <CardHeader>
          <CardTitle>Campaigns</CardTitle>
          <CardDescription>Manage your AI voice call campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No campaigns yet. Create your first AI voice campaign!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  data-testid={`campaign-${campaign.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{campaign.name}</h3>
                        <Badge className={statusColors[campaign.status]}>{campaign.status}</Badge>
                      </div>
                      {campaign.description && (
                        <p className="text-sm text-slate-600 mb-3">{campaign.description}</p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Phone className="h-4 w-4" />
                          {campaign.calls_made || 0} calls
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-indigo-500" />
                          {campaign.total_leads || campaign.lead_ids?.length || 0} leads
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          {campaign.meetings_booked || 0} meetings
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-blue-500" />
                          {campaign.demos_scheduled || 0} demos
                        </span>
                        <span className="flex items-center gap-1">
                          <Voicemail className="h-4 w-4 text-orange-500" />
                          {campaign.voicemails_dropped || 0} voicemails
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {campaign.call_window_start} - {campaign.call_window_end}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {campaign.dialer_mode === 'predictive' && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => launchLiveSession(campaign.id)}
                          title={campaign.live_session_state && campaign.live_session_state !== 'idle'
                            ? `Already live (${campaign.live_session_state}) — opening dashboard`
                            : 'Launch predictive dialer'}
                          disabled={!campaign.voicemail_telnyx_media_name || !campaign.pitch_telnyx_media_name}
                        >
                          <PhoneCall className="h-4 w-4 mr-1" />
                          {campaign.live_session_state && campaign.live_session_state !== 'idle' ? 'Open live' : 'Launch live'}
                        </Button>
                      )}
                      {campaign.status === 'draft' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(campaign.id, 'active')}
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Activate
                        </Button>
                      )}
                      {campaign.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(campaign.id, 'paused')}
                        >
                          <Pause className="h-4 w-4 mr-1" />
                          Pause
                        </Button>
                      )}
                      {campaign.status === 'paused' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(campaign.id, 'active')}
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Resume
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(campaign)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(campaign.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-4">
          <AICallAnalytics />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Campaign Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setCreateDialogOpen(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              {editingCampaign ? 'Edit Campaign' : 'Create AI Voice Campaign'}
            </DialogTitle>
            <DialogDescription>
              Configure your AI sales agent for outbound calls
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="voice">Voice & Script</TabsTrigger>
              <TabsTrigger value="leads" onClick={fetchLeads}>
                Leads {formData.lead_ids?.length > 0 && `(${formData.lead_ids.length})`}
              </TabsTrigger>
              <TabsTrigger value="dialer" onClick={fetchAudioLibrary}>Dialer</TabsTrigger>
              <TabsTrigger value="followup">Follow-up</TabsTrigger>
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Campaign Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Q1 Staffing Outreach"
                  data-testid="campaign-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Campaign objective and target audience..."
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="voice" className="space-y-4 mt-4">
              {/* Voice Selection */}
              <div className="space-y-3">
                <Label>AI Voice *</Label>
                <div className="flex gap-2 mb-2">
                  <Button
                    type="button"
                    variant={selectedGender === '' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleGenderChange('')}
                  >
                    All
                  </Button>
                  <Button
                    type="button"
                    variant={selectedGender === 'male' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleGenderChange('male')}
                  >
                    Male
                  </Button>
                  <Button
                    type="button"
                    variant={selectedGender === 'female' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleGenderChange('female')}
                  >
                    Female
                  </Button>
                </div>
                
                {loadingVoices ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {voices.map((voice) => (
                      <div
                        key={voice.voice_id}
                        className={`p-2 rounded-lg border transition-all flex items-center gap-2 ${
                          formData.voice_id === voice.voice_id
                            ? 'border-primary bg-primary/10 ring-2 ring-primary'
                            : 'border-gray-200 hover:border-primary/50'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVoicePreview(voice.voice_id, voice.name);
                          }}
                          className={`p-1.5 rounded-full flex-shrink-0 transition-colors ${
                            previewingVoiceId === voice.voice_id 
                              ? 'bg-primary text-white' 
                              : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                          }`}
                          title={previewingVoiceId === voice.voice_id ? 'Stop preview' : 'Preview voice'}
                        >
                          {previewingVoiceId === voice.voice_id ? (
                            <Volume2 className="h-3 w-3 animate-pulse" />
                          ) : (
                            <Play className="h-3 w-3" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, voice_id: voice.voice_id, voice_name: voice.name })}
                          className="text-left flex-1 min-w-0"
                        >
                          <span className="font-medium text-sm block truncate">{voice.name}</span>
                          <span className="text-xs text-slate-500">({voice.gender})</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* System Prompt */}
              <div className="space-y-2">
                <Label>System Prompt (AI Instructions) *</Label>
                <Textarea
                  value={formData.system_prompt}
                  onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                  placeholder="Instructions for the AI agent..."
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">
                  Define how the AI should behave, what information it should know, and how to handle objections.
                </p>
              </div>

              {/* Opening Message */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Opening Message *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateAIOpeningMessage}
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Generate with AI
                  </Button>
                </div>
                <Textarea
                  value={formData.opening_message}
                  onChange={(e) => {
                    setFormData({ ...formData, opening_message: e.target.value });
                    // Clear generated audio when message changes
                    if (greetingAudioUrl) {
                      setGreetingAudioUrl('');
                      setFormData(prev => ({ ...prev, greeting_audio_url: '' }));
                    }
                  }}
                  placeholder="Hi, this is an AI assistant calling on behalf of JobTalk..."
                  rows={4}
                />
                <p className="text-xs text-slate-500">
                  The first thing the AI will say when a human answers.
                </p>

                {/* Greeting name input */}
                <div className="pt-2 space-y-1">
                  <Label className="text-xs text-slate-600">Greeting name (optional)</Label>
                  <Input
                    value={formData.greeting_audio_name || ''}
                    onChange={(e) => setFormData({ ...formData, greeting_audio_name: e.target.value })}
                    placeholder="e.g., Friendly intro v2"
                    className="max-w-md"
                  />
                </div>

                {/* Generate & Preview Greeting Audio */}
                {!(greetingAudioUrl || formData.greeting_audio_url) ? (
                  // No audio yet - show generate button
                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateGreeting}
                      disabled={generatingGreeting || !formData.voice_id || !formData.opening_message.trim()}
                    >
                      {generatingGreeting ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Volume2 className="h-4 w-4 mr-1" />
                      )}
                      {generatingGreeting ? 'Generating...' : 'Generate Audio'}
                    </Button>
                    {!formData.voice_id && (
                      <span className="text-xs text-amber-600">Select a voice first</span>
                    )}
                  </div>
                ) : (
                  // Audio exists - show player and controls
                  <div className="pt-2 space-y-3">
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Audio Ready
                          </Badge>
                          <span className="text-xs text-slate-700 font-medium">
                            {formData.greeting_audio_name || formData.greeting_audio_filename || 'greeting.mp3'}
                          </span>
                          {editingCampaign?.id && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-slate-500 hover:text-slate-800"
                              onClick={async () => {
                                const next = window.prompt('Rename greeting:', formData.greeting_audio_name || '');
                                if (next === null) return;
                                const trimmed = next.trim();
                                if (!trimmed) return;
                                try {
                                  await axios.patch(`${API}/api/ai-calls/greeting-name`, {
                                    name: trimmed,
                                    campaign_id: editingCampaign.id,
                                  });
                                  setFormData(prev => ({ ...prev, greeting_audio_name: trimmed }));
                                  toast.success('Renamed');
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Rename failed');
                                }
                              }}
                            >
                              Rename
                            </Button>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleDeleteGreeting}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                      
                      {/* Audio Player */}
                      <audio 
                        controls 
                        className="w-full h-10"
                        src={greetingAudioUrl || formData.greeting_audio_url}
                      >
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteGreeting}
                      className="text-slate-600"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Regenerate Audio
                    </Button>
                  </div>
                )}
              </div>

              {/* Test Call Section */}
              <div className="border-t pt-4 mt-4">
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <PhoneCall className="h-4 w-4 text-green-600" />
                    Test Your AI Voice
                  </Label>
                  <p className="text-xs text-slate-500">
                    Make a test call to hear how your AI agent sounds. {!(greetingAudioUrl || formData.greeting_audio_url) && <span className="text-amber-600 font-medium">Generate greeting audio first!</span>}
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={testPhoneNumber}
                      onChange={(e) => setTestPhoneNumber(e.target.value)}
                      placeholder="Phone number"
                      className="max-w-[200px]"
                      data-testid="test-phone-input"
                    />
                    <Button
                      type="button"
                      onClick={handleTestCall}
                      disabled={testCallLoading || !formData.voice_id || !formData.opening_message.trim()}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="test-call-btn"
                    >
                      {testCallLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Phone className="h-4 w-4 mr-2" />
                          TEST
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="leads" className="space-y-4 mt-4">
              {/* Leads Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Campaign Leads
                    </Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Select leads to include in this campaign.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-lg px-3 py-1">
                      {formData.lead_ids?.length || 0} selected
                    </Badge>
                    {formData.lead_ids?.length > 0 && (
                      <Badge className="bg-green-100 text-green-700">
                        {formData.lead_ids.filter(id => {
                          const lead = allLeads.find(l => l.id === id);
                          return lead && (lead.work_direct_phone || lead.mobile_phone || lead.work_company_phone) && !lead.dnc;
                        }).length} callable
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Search and Actions */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Target className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={leadSearchQuery}
                      onChange={(e) => setLeadSearchQuery(e.target.value)}
                      placeholder="Search leads by name, company, email, phone..."
                      className="pl-9"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={selectAllFilteredLeads}
                    disabled={filteredLeads.length === 0}
                  >
                    Select All ({filteredLeads.length})
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearAllLeads}
                    disabled={!formData.lead_ids?.length}
                  >
                    Clear All
                  </Button>
                </div>

                {/* Leads List */}
                {loadingLeads ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <span className="ml-2 text-slate-500">Loading leads...</span>
                  </div>
                ) : filteredLeads.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>{allLeads.length === 0 ? 'No leads found in CRM' : 'No leads match your search'}</p>
                  </div>
                ) : (
                  <div className="border rounded-lg max-h-[350px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="w-10 p-2"></th>
                          <th className="text-left p-2 font-medium">Name</th>
                          <th className="text-left p-2 font-medium">Company</th>
                          <th className="text-left p-2 font-medium">Email</th>
                          <th className="text-left p-2 font-medium">Phone</th>
                          <th className="text-left p-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLeads.map((lead) => {
                          const isSelected = formData.lead_ids?.includes(lead.id);
                          const phone = lead.work_direct_phone || lead.mobile_phone || lead.work_company_phone;
                          const hasPhone = !!phone;
                          const isDNC = lead.dnc;
                          
                          return (
                            <tr 
                              key={lead.id} 
                              className={`border-t cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-primary/5' : ''} ${isDNC ? 'opacity-60' : ''}`}
                              onClick={() => toggleLeadSelection(lead.id)}
                            >
                              <td className="p-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="h-4 w-4 rounded border-gray-300"
                                />
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-1">
                                  <span className="font-medium">{lead.first_name} {lead.last_name}</span>
                                  {isDNC && (
                                    <Badge variant="destructive" className="text-[10px] px-1 py-0">DNC</Badge>
                                  )}
                                </div>
                                {lead.title && <span className="text-xs text-slate-500 block">{lead.title}</span>}
                              </td>
                              <td className="p-2 text-slate-600 max-w-[120px] truncate">{lead.company_name || '—'}</td>
                              <td className="p-2 text-slate-600 text-xs max-w-[150px] truncate">{lead.work_email || '—'}</td>
                              <td className="p-2">
                                {hasPhone ? (
                                  <span className="text-slate-600 font-mono text-xs">{phone}</span>
                                ) : (
                                  <Badge variant="outline" className="text-amber-600 border-amber-200 text-[10px]">No Phone</Badge>
                                )}
                              </td>
                              <td className="p-2">
                                <Badge variant="outline" className="text-xs">
                                  {lead.status || 'new'}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Summary Stats */}
                <div className="flex gap-4 text-sm">
                  <span className="text-slate-500">
                    Total: <strong>{allLeads.length}</strong> leads
                  </span>
                  <span className="text-slate-500">
                    With Phone: <strong>{allLeads.filter(l => l.work_direct_phone || l.mobile_phone || l.work_company_phone).length}</strong>
                  </span>
                  <span className="text-slate-500">
                    DNC: <strong>{allLeads.filter(l => l.dnc).length}</strong>
                  </span>
                </div>

                {/* Quick Stats */}
                {formData.lead_ids?.length > 0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                    <strong>{formData.lead_ids.length} leads selected</strong> — 
                    {(() => {
                      const callable = formData.lead_ids.filter(id => {
                        const lead = allLeads.find(l => l.id === id);
                        return lead && (lead.work_direct_phone || lead.mobile_phone || lead.work_company_phone) && !lead.dnc;
                      }).length;
                      const noPhone = formData.lead_ids.filter(id => {
                        const lead = allLeads.find(l => l.id === id);
                        return lead && !(lead.work_direct_phone || lead.mobile_phone || lead.work_company_phone);
                      }).length;
                      const dnc = formData.lead_ids.filter(id => {
                        const lead = allLeads.find(l => l.id === id);
                        return lead && lead.dnc;
                      }).length;
                      return (
                        <span>
                          {' '}{callable} will be called
                          {noPhone > 0 && <span className="text-amber-600">, {noPhone} have no phone</span>}
                          {dnc > 0 && <span className="text-red-600">, {dnc} are DNC</span>}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="dialer" className="space-y-6 mt-4">
              <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
                <Label className="text-base font-semibold">Dialer Mode</Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={formData.dialer_mode === 'preview' ? 'default' : 'outline'}
                    onClick={() => setFormData({ ...formData, dialer_mode: 'preview' })}
                  >Preview</Button>
                  <Button
                    type="button"
                    variant={formData.dialer_mode === 'predictive' ? 'default' : 'outline'}
                    onClick={() => setFormData({ ...formData, dialer_mode: 'predictive' })}
                  >Predictive (live)</Button>
                </div>
                <p className="text-sm text-slate-600">
                  <b>Predictive</b>: places N concurrent calls, detects voicemails and drops a clip,
                  parks live humans on a conference + plays your pitch while you pick up in the browser.
                </p>
              </div>

              {formData.dialer_mode === 'predictive' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Max concurrent calls *</Label>
                      <Input
                        type="number" min={1} max={20}
                        value={formData.max_concurrent_calls}
                        onChange={(e) => setFormData({ ...formData, max_concurrent_calls: parseInt(e.target.value || '4', 10) })}
                      />
                      <p className="text-xs text-slate-500">Actual dial rate adapts with voicemail hit-rate (0.5×–3× of this).</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Pitch grace period (seconds)</Label>
                      <Input
                        type="number" min={0} max={30}
                        value={formData.pitch_grace_seconds}
                        onChange={(e) => setFormData({ ...formData, pitch_grace_seconds: parseInt(e.target.value || '8', 10) })}
                      />
                      <p className="text-xs text-slate-500">After the pitch ends, keep conference open this long waiting for you to Answer; otherwise drop voicemail.</p>
                    </div>
                  </div>

                  {['voicemail', 'pitch'].map((kind) => {
                    const label = kind === 'voicemail' ? 'Voicemail clip (plays into answering machines)' : 'Pitch clip (plays to live humans while you pick up)';
                    const currentId = formData[`${kind}_media_id`];
                    const clips = audioLibrary[kind] || [];
                    return (
                      <div key={kind} className="space-y-3 border rounded-lg p-4">
                        <Label className="flex items-center gap-2 font-semibold">
                          <Voicemail className="h-4 w-4" />{label}
                        </Label>

                        {clips.length > 0 ? (
                          <div className="space-y-2">
                            {clips.map(c => {
                              const isSelected = c.id === currentId;
                              return (
                                <div
                                  key={c.id}
                                  className={`flex items-center gap-2 p-2 rounded border ${isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white'}`}
                                >
                                  <Button
                                    type="button"
                                    variant={isSelected ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => selectAudioClip(kind, c.id)}
                                    className="h-8 px-2 min-w-[80px]"
                                  >
                                    {isSelected ? (<><CheckCircle2 className="h-3 w-3 mr-1" />Selected</>) : 'Select'}
                                  </Button>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{c.name}</div>
                                    <div className="text-xs text-slate-500">
                                      {c.duration_seconds ? `${c.duration_seconds.toFixed(1)}s` : '—'}
                                      {c.file_size_bytes ? ` · ${(c.file_size_bytes / 1024).toFixed(0)} KB` : ''}
                                    </div>
                                  </div>
                                  <audio
                                    controls
                                    preload="none"
                                    src={`${API}/api/ai-calls/audio-media/${c.id}/audio`}
                                    className="h-8 w-44"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    title="Rename"
                                    onClick={() => renameAudioClip(kind, c.id, c.name)}
                                    className="h-8 px-2 text-slate-500 hover:text-slate-800"
                                  >
                                    Rename
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    title="Delete"
                                    onClick={() => deleteAudioClip(kind, c.id)}
                                    className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-500 italic">No clips yet — upload one below.</div>
                        )}

                        <div className="flex items-center gap-2 pt-1">
                          <Input
                            type="file"
                            accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) {
                                const baseName = (f.name || `${kind}.mp3`).replace(/\.[^/.]+$/, '');
                                uploadAudioClip(kind, f, baseName);
                              }
                              e.target.value = '';
                            }}
                            disabled={uploadingAudio[kind]}
                          />
                          {uploadingAudio[kind] && <Loader2 className="h-4 w-4 animate-spin" />}
                        </div>
                        <p className="text-xs text-slate-500">MP3 or WAV, ≤ 2 MB, ≤ 60s. The uploaded file's name becomes the clip name — click "Rename" to change it later.</p>
                      </div>
                    );
                  })}

                  <div className="text-sm text-slate-600 border rounded p-3 bg-amber-50">
                    <div className="font-semibold mb-1">TCPA safety</div>
                    Dialer auto-pauses if the abandonment rate exceeds {formData.abandonment_cap_pct}%.
                    Leads flagged <code>dnc</code> / <code>bad_number</code> / <code>unreachable</code> are skipped.
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="followup" className="space-y-6 mt-4">
              {/* Voicemail Template */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Voicemail className="h-4 w-4" />
                  Voicemail Template (for answering machines)
                </Label>
                <Select
                  value={formData.voicemail_template_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, voicemail_template_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a voicemail template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (AI will hang up)</SelectItem>
                    {voicemailTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Email Follow-up Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-500" />
                  Email Follow-up Automation
                </h4>
                <p className="text-xs text-slate-500">
                  Automatically send emails via O365 based on call outcomes. Uses default templates if none selected.
                </p>

                {/* Email Triggers with Templates */}
                <div className="grid gap-3">
                  {/* Voicemail Email */}
                  <div className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
                    <Switch
                      checked={formData.send_email_on_voicemail}
                      onCheckedChange={(checked) => setFormData({ ...formData, send_email_on_voicemail: checked })}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium">When voicemail dropped</span>
                      {formData.send_email_on_voicemail && (
                        <Select
                          value={formData.email_template_voicemail || 'default'}
                          onValueChange={(value) => setFormData({ ...formData, email_template_voicemail: value === 'default' ? '' : value })}
                        >
                          <SelectTrigger className="mt-2 h-8">
                            <SelectValue placeholder="Use default template" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Use default template</SelectItem>
                            {emailTemplates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>

                  {/* No Answer Email */}
                  <div className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
                    <Switch
                      checked={formData.send_email_on_no_answer}
                      onCheckedChange={(checked) => setFormData({ ...formData, send_email_on_no_answer: checked })}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium">When no answer (all attempts)</span>
                      {formData.send_email_on_no_answer && (
                        <Select
                          value={formData.email_template_no_answer || 'default'}
                          onValueChange={(value) => setFormData({ ...formData, email_template_no_answer: value === 'default' ? '' : value })}
                        >
                          <SelectTrigger className="mt-2 h-8">
                            <SelectValue placeholder="Use default template" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Use default template</SelectItem>
                            {emailTemplates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>

                  {/* Interested Email */}
                  <div className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
                    <Switch
                      checked={formData.send_email_on_interested}
                      onCheckedChange={(checked) => setFormData({ ...formData, send_email_on_interested: checked })}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium">When lead shows interest</span>
                      {formData.send_email_on_interested && (
                        <Select
                          value={formData.email_template_interested || 'default'}
                          onValueChange={(value) => setFormData({ ...formData, email_template_interested: value === 'default' ? '' : value })}
                        >
                          <SelectTrigger className="mt-2 h-8">
                            <SelectValue placeholder="Use default template" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Use default template</SelectItem>
                            {emailTemplates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>

                  {/* Callback Email */}
                  <div className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
                    <Switch
                      checked={formData.send_email_on_callback}
                      onCheckedChange={(checked) => setFormData({ ...formData, send_email_on_callback: checked })}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium">When callback requested</span>
                      {formData.send_email_on_callback && (
                        <Select
                          value={formData.email_template_callback || 'default'}
                          onValueChange={(value) => setFormData({ ...formData, email_template_callback: value === 'default' ? '' : value })}
                        >
                          <SelectTrigger className="mt-2 h-8">
                            <SelectValue placeholder="Use default template" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Use default template</SelectItem>
                            {emailTemplates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>

                  {/* Meeting Booked Email */}
                  <div className="flex items-center gap-3 p-3 border rounded-lg bg-green-50">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">When meeting/demo booked</span>
                      <span className="text-xs text-slate-500 block">Always sent</span>
                      <Select
                        value={formData.email_template_meeting_booked || 'default'}
                        onValueChange={(value) => setFormData({ ...formData, email_template_meeting_booked: value === 'default' ? '' : value })}
                      >
                        <SelectTrigger className="mt-2 h-8">
                          <SelectValue placeholder="Use default template" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Use default template</SelectItem>
                          {emailTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* SMS Follow-up Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Phone className="h-4 w-4 text-green-500" />
                  SMS Follow-up (via Telnyx)
                </h4>
                <p className="text-xs text-slate-500">
                  Send SMS follow-ups to leads with mobile numbers.
                </p>

                <div className="grid gap-3">
                  {/* Voicemail SMS */}
                  <div className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
                    <Switch
                      checked={formData.send_sms_on_voicemail}
                      onCheckedChange={(checked) => setFormData({ ...formData, send_sms_on_voicemail: checked })}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium">When voicemail dropped</span>
                      {formData.send_sms_on_voicemail && (
                        <Select
                          value={formData.sms_template_voicemail || 'default'}
                          onValueChange={(value) => setFormData({ ...formData, sms_template_voicemail: value === 'default' ? '' : value })}
                        >
                          <SelectTrigger className="mt-2 h-8">
                            <SelectValue placeholder="Use default message" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Use default message</SelectItem>
                            {smsTemplates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>

                  {/* No Answer SMS */}
                  <div className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
                    <Switch
                      checked={formData.send_sms_on_no_answer}
                      onCheckedChange={(checked) => setFormData({ ...formData, send_sms_on_no_answer: checked })}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium">When no answer</span>
                      {formData.send_sms_on_no_answer && (
                        <Select
                          value={formData.sms_template_no_answer || 'default'}
                          onValueChange={(value) => setFormData({ ...formData, sms_template_no_answer: value === 'default' ? '' : value })}
                        >
                          <SelectTrigger className="mt-2 h-8">
                            <SelectValue placeholder="Use default message" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Use default message</SelectItem>
                            {smsTemplates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="schedule" className="space-y-4 mt-4">
              {/* Call Window */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Call Window Start</Label>
                  <Input
                    type="time"
                    value={formData.call_window_start}
                    onChange={(e) => setFormData({ ...formData, call_window_start: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Call Window End</Label>
                  <Input
                    type="time"
                    value={formData.call_window_end}
                    onChange={(e) => setFormData({ ...formData, call_window_end: e.target.value })}
                  />
                </div>
              </div>

              {/* Call Days */}
              <div className="space-y-2">
                <Label>Active Days</Label>
                <div className="flex flex-wrap gap-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                    <Button
                      key={day}
                      type="button"
                      variant={formData.call_days.includes(day) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        const newDays = formData.call_days.includes(day)
                          ? formData.call_days.filter(d => d !== day)
                          : [...formData.call_days, day];
                        setFormData({ ...formData, call_days: newDays });
                      }}
                    >
                      {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Retry Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Attempts</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={formData.max_attempts}
                    onChange={(e) => setFormData({ ...formData, max_attempts: parseInt(e.target.value) || 3 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Retry Interval (hours)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    value={formData.retry_interval_hours}
                    onChange={(e) => setFormData({ ...formData, retry_interval_hours: parseInt(e.target.value) || 24 })}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function getDefaultSystemPrompt() {
  return `You are an AI sales assistant for JobTalk.ai. Your goal is to schedule a meeting with the CEO, Taj Haslani, or a product demo.

Key facts about JobTalk:
- JobTalk AI can make hundreds of calls in minutes
- Helps recruiters double or triple their placements
- Contacts candidates within minutes of applying
- Works with ALL ATS systems
- Can extract resumes and contact details from ATS
- Campaigns start within minutes
- Instantly connects candidates to recruiters during business hours
- Handles 24x7x365 incoming calls
- Enterprise customers: 10K-15K calls/day, 75-100 placements/day

Be professional, concise, and focused on ROI and scale, not features.
If the person is not interested, politely thank them and end the call.
If they want to be removed from the list, acknowledge and note "DO NOT CALL".
If they're busy, offer to call back at a better time.`;
}
